import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../dates/DatesContext";
import { useFetch } from "../../../hooks/api";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import getMetricSpec from "../../merchandising/utils/metrics";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import LargeValue from "../../../components/core/blocks/LargeValue";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import moment from "moment";
import { Row, Col, Nav } from "react-bootstrap";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import BiddingProgramLog from "../../../components/custom/programs/bidding/BiddingProgramLog";
import { useAppSelector } from "../../../redux/hooks";
import { Chart } from "@highcharts/react";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { Accordion } from "../../../components/core/basic/Accordion";
import { keepPreviousData } from "@tanstack/react-query";
import { useFormatTargetText } from "../../../utils/useFormatTargetText";

const ImpressionCurve = ({ data }) => {
  const { t } = useTranslation(["advertising"]);
  // Guard against undefined or empty data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div>
        <h5 className="text-center">{t(($) => $.advertising["Impression Curve"])}</h5>
        <p className="text-center text-muted">{t(($) => $.advertising["No impression curve data available"])}</p>
      </div>
    );
  }

  const options = {
    title: { text: "" },
    colors: trellisPalette,
    chart: {
      type: "line",
      style: {
        fontFamily: "Poppins",
        fontWeight: 300,
        fontSize: "15px",
      },
    },
    xAxis: {
      categories: data.map((d) => d.bin_mid),
    },
    yAxis: [
      {
        title: {
          text: t(($) => $.advertising["Impressions"]),
        },
      },
      {
        title: {
          text: t(($) => $.advertising["Profitability"]),
        },
        opposite: true,
      },
    ],
    series: [
      {
        name: t(($) => $.advertising["Effective Impressions"]),
        data: data.map((d) => d.eff_impressions),
      },
      {
        name: t(($) => $.advertising["Impression Rate"]),
        data: data.map((d) => d.imp_rate),
      },
      {
        name: t(($) => $.advertising["Smooth Impression Rate"]),
        data: data.map((d) => d.smooth_imp_rate),
      },
      {
        name: t(($) => $.advertising["Smooth Impressions"]),
        data: data.map((d) => d.smooth_impressions),
      },
      {
        name: t(($) => $.advertising["Profitablity"]),
        data: data.map((d) => d.multiplier_curve),
        yAxis: 1,
      },
      {
        name: t(($) => $.advertising["Profit Curve"]),
        data: data.map((d) => d.modeled_profit_curve),
        yAxis: 1,
      },
    ],
  };
  return (
    <div>
      <h5 className="text-center">{t(($) => $.advertising["Impression Curve"])}</h5>
      <Chart options={options} />
    </div>
  );
};

const TargetDetails = ({ row, targetType, marketPlace = { marketPlace: "USA" }, channel = "walmart" }) => {
  const { t } = useTranslation(["advertising"]);
  const formatTargetText = useFormatTargetText();
  const [tab, setTab] = useState("overview");

  const isStaff = useAppSelector((state) => state.user?.is_staff ?? false);
  const debugModule = useModuleEnabled("DEBUG");
  const canViewDebug = isStaff && debugModule;

  const acosOrRoas = useAcosOrRoas();

  const { text, target_id, match_type, refinements } = row._original ?? {};

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    target_id: target_id,
    target_type: targetType,
    kw_report: false,
    channel: channel,
  };

  const { data: targetData, isLoading } = useFetch(
    [
      "keyword_report",
      filters.start_date,
      filters.end_date,
      filters.pre_start_date,
      filters.pre_end_date,
      group,
      target_id,
    ],
    `/api/data_report/keyword_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!target_id,
      staleTime: 2 * 60 * 1000,
    },
  );

  const { data: targetBidData } = useFetch(["target_details", target_id], "/target_details", filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
    enabled: !!target_id,
    staleTime: 2 * 60 * 1000,
  });

  const bidhistoryFilters = { limit: 1 };
  if (targetType === "keyword") {
    bidhistoryFilters["adv_keyword"] = target_id;
  } else {
    bidhistoryFilters["adv_target"] = target_id;
  }
  const { data: bidhistory } = useFetch(["bidding", targetType, target_id], "/bidhistory", bidhistoryFilters, {
    select: (d) => d.data?.results?.[0],
    enabled: !!target_id && canViewDebug,
  });

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(["ad_sales", "clicks", "daily_bid", "impressions"]);

  const metrics = useMemo(
    () => [
      getMetricSpec("clicks"),
      getMetricSpec("ad_spend"),
      getMetricSpec("orders"),
      getMetricSpec("daily_bid"),
      getMetricSpec("impressions"),
      getMetricSpec("ad_sales"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
      getMetricSpec("ctr"),
      getMetricSpec("conversion_rate"),
      getMetricSpec("cpc"),
    ],
    [acosOrRoas],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const dateFilterWithoutToday =
    targetData?.date
      ?.map((d) => ({ ...d, start_date: d.start_date?.slice(0, 10) }))
      ?.filter((d) => moment().format("YYYY-MM-DD") !== d.start_date) ?? [];

  // details exclude exploitation_curves
  const details = bidhistory ? { ...bidhistory } : {};
  details.exploitation_curves = undefined;

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row className="pt-3">
      <Col xs={12}>
        <h3
          className="pb-4 px-5"
          style={{
            fontSize: 22,
          }}
        >
          {formatTargetText(text, { ...refinements, marketPlace: marketPlace.marketPlace })} - {match_type}
        </h3>
      </Col>
      <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom fs-standard">
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setTab(k);
          }}
          activeKey={tab}
        >
          <Nav.Item>
            <Nav.Link eventKey="overview">{t(($) => $.advertising["Overview"])}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="logs">{t(($) => $.advertising["Bidding Rules History"])}</Nav.Link>
          </Nav.Item>
          {canViewDebug && (
            <Nav.Item>
              <Nav.Link eventKey="debug">{t(($) => $.advertising["Bidding Parameters"])}</Nav.Link>
            </Nav.Item>
          )}
        </Nav>
      </Col>

      {tab === "overview" && (
        <>
          <Col xs={12}>
            <MultiMetricChart
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              data={dateFilterWithoutToday}
              group={group}
              handleLegendChange={handleLegendChange}
              showAnnotations={false}
            />
          </Col>
          <Col xs={12}>
            <Row className="pt-4">
              {metrics
                ?.filter((m) => !["daily_bid", "ctr", "conversion_rate", "cpc"].includes(m.id))
                ?.map((m, i) => {
                  return (
                    <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                      <div className="">
                        <div>
                          <DetailedMetric center metric={m} data={targetData?.total} showTarget={false} />
                        </div>
                      </div>
                    </Col>
                  );
                })}
              {targetBidData?.manual_bid && (
                <Col xs={12} md={6} lg={2} className={`mb-4 pe-2`}>
                  <div className="">
                    <div>
                      <LargeValue
                        center
                        size={"small"}
                        id={"manual_bid"}
                        name={t(($) => $.advertising["Manual Bid"])}
                        value={formatCurrency(targetBidData?.manual_bid, {
                          marketPlace: marketPlace.marketPlace,
                          compact: true,
                        })}
                      />
                    </div>
                  </div>
                </Col>
              )}
              {targetBidData?.floor_bid && (
                <Col xs={12} md={6} lg={2} className={`mb-4 pe-2`}>
                  <div>
                    <div>
                      <LargeValue
                        center
                        size={"small"}
                        id={"floor_bid"}
                        name={t(($) => $.advertising["Minimum Bid"])}
                        value={formatCurrency(targetBidData?.floor_bid, {
                          marketPlace: marketPlace.marketPlace,
                          compact: true,
                        })}
                      />
                    </div>
                  </div>
                </Col>
              )}
              {targetBidData?.ceil_bid && (
                <Col xs={12} md={6} lg={2} className={`mb-4 pe-2`}>
                  <div>
                    <div>
                      <LargeValue
                        center
                        size={"small"}
                        id={"ceil_bid"}
                        name={t(($) => $.advertising["Maximum Bid"])}
                        value={formatCurrency(targetBidData?.ceil_bid, {
                          marketPlace: marketPlace.marketPlace,
                          compact: true,
                        })}
                      />
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Col>
        </>
      )}
      {tab === "logs" && (
        <Col xs={12} style={{ minHeight: "250px" }} className="px-5">
          <Row>
            {/* targetId => adv_keyword or adv_target */}
            <BiddingProgramLog targetId={target_id} targetType={targetType} marketPlace={marketPlace} />
          </Row>
        </Col>
      )}
      {tab === "debug" && (
        <Col xs={12} className="fs-standard px-5">
          <Row>
            <Col xs={4} className="pt-3">
              <h5 className="text-center">{t(($) => $.advertising["Bid Details"])}</h5>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <th>{t(($) => $.advertising["Current Bid"])}</th>
                    <td>
                      {formatCurrency(bidhistory?.new_bid, {
                        marketPlace: marketPlace.marketPlace,
                        compact: true,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <th>{t(($) => $.advertising["Bidding Stage"])}</th>
                    <td>{bidhistory?.history?.bid_stage}</td>
                  </tr>
                  {bidhistory?.history?.bid_stage?.startsWith("v1") && (
                    <>
                      <tr>
                        <th>{t(($) => $.advertising["ACOS Bid"])}</th>
                        <td>
                          {formatCurrency(bidhistory?.history?.acos_bid, {
                            marketPlace: marketPlace.marketPlace,
                            compact: true,
                          })}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.advertising["Effective CR"])}</th>
                        <td>{formatPercent(bidhistory?.history?.effective_cr, { decimalPlaces: 1 })}</td>
                      </tr>
                    </>
                  )}
                  {bidhistory?.history?.bid_stage?.startsWith("v2") && (
                    <>
                      <tr>
                        <th>{t(($) => $.advertising["Optimal Bid"])}</th>
                        <td>
                          {formatCurrency(bidhistory?.history?.optimal_bid, {
                            marketPlace: marketPlace.marketPlace,
                            compact: true,
                          })}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.advertising["CR Status"])}</th>
                        <td>{bidhistory?.history?.cr_status}</td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.advertising["End Bid"])}</th>
                        <td>
                          {formatCurrency(bidhistory?.history?.end_bid, {
                            marketPlace: marketPlace.marketPlace,
                            compact: true,
                          })}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <th>{t(($) => $.advertising["Target CR"])}</th>
                    <td>{formatPercent(bidhistory?.history?.kw_cr, { decimalPlaces: 1 })}</td>
                  </tr>
                  <tr>
                    <th>{t(($) => $.advertising["CR 30 days"])}</th>
                    <td>{formatPercent(bidhistory?.history?.cr_30d, { decimalPlaces: 1 })}</td>
                  </tr>
                  <tr>
                    <th>{t(($) => $.advertising["CR 180 days"])}</th>
                    <td>{formatPercent(bidhistory?.history?.cr_180d, { decimalPlaces: 1 })}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
            <Col xs={8}>
              <ImpressionCurve data={bidhistory?.exploitation_curves} />
            </Col>
            <Col xs={12} className="py-4">
              <Accordion title={t(($) => $.advertising["debug"])}>
                {/* exclude the key exploitation_curves */}
                <pre className="bg-light p-2">{JSON.stringify(details, null, 2)}</pre>
              </Accordion>
            </Col>
          </Row>
        </Col>
      )}
    </Row>
  );
};

export default TargetDetails;
